import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import NfcRoundedIcon from '@mui/icons-material/NfcRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import ErrorRoundedIcon from '@mui/icons-material/ErrorRounded';
import HowToRegRoundedIcon from '@mui/icons-material/HowToRegRounded';
import AppLayout from '../../components/ui/AppLayout';
import PageHeader from '../../components/ui/PageHeader';
import SectionCard from '../../components/ui/SectionCard';
import api from '../../api';
import tokens from '../../theme/tokens';

// Localização do aparelho (só quando a API pede: Configurações › CHECKIN_NFC_VALIDAR_LOCAL)
const obterLocalizacao = () =>
  new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error('Este navegador não informa a localização, que é necessária para o check-in.'));
      return;
    }
    navigator.geolocation.getCurrentPosition(
      ({ coords }) => resolve({ latitude: coords.latitude, longitude: coords.longitude, precisao: coords.accuracy }),
      (error) =>
        reject(new Error(error.code === 1 ? 'Permita o acesso à localização para fazer o check-in.' : 'Não foi possível obter a sua localização. Tente de novo.')),
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 30000 }
    );
  });

const ESTILO = {
  processando: { cor: tokens.color.info, fundo: tokens.color.infoSoft },
  sucesso: { cor: tokens.color.success, fundo: tokens.color.successSoft },
  aviso: { cor: tokens.color.warning, fundo: tokens.color.warningSoft },
  erro: { cor: tokens.color.danger, fundo: tokens.color.dangerSoft },
};

/**
 * /checkin/nfc/:codigo — aberta ao encostar o celular na tag NFC da concentração.
 * Faz o check-in do usuário logado na hora; se ele ainda não confirmou presença, oferece confirmar e entrar.
 */
const CheckinNfcPage = () => {
  const { codigo } = useParams();
  const navigate = useNavigate();
  const [estado, setEstado] = useState({ tipo: 'processando', titulo: 'Fazendo o seu check-in...' });
  const iniciado = useRef(false);
  const user = JSON.parse(localStorage.getItem('user') || 'null');

  const posicaoNaFila = async () => {
    try {
      const { data } = await api.get('getPeladaActive');
      const chegadas = (data.data?.arrivedPlayers || []).filter((p) => !p.saiu);
      const indice = chegadas.findIndex((p) => !p.guestId && String(p.user) === String(user?._id));
      return indice >= 0 ? indice + 1 : null;
    } catch (error) {
      return null;
    }
  };

  const fazerCheckin = useCallback(async () => {
    setEstado({ tipo: 'processando', titulo: 'Fazendo o seu check-in...' });
    try {
      // Tenta sem localização; se a API exigir (LOCAL_OBRIGATORIO), pega a posição e tenta de novo
      let data;
      try {
        ({ data } = await api.post('v2/checkin/nfc', { codigo }));
      } catch (error) {
        if (error.response?.data?.code !== 'LOCAL_OBRIGATORIO') throw error;
        setEstado({ tipo: 'processando', titulo: 'Conferindo a sua localização...' });
        const local = await obterLocalizacao();
        ({ data } = await api.post('v2/checkin/nfc', { codigo, ...local }));
      }
      if (data.jaFeito) {
        setEstado({ tipo: 'aviso', titulo: 'Check-in já feito', texto: data.message });
        return;
      }
      const posicao = await posicaoNaFila();
      setEstado({
        tipo: 'sucesso',
        titulo: 'Check-in feito!',
        texto: posicao ? `Você é o nº ${posicao} na ordem de chegada.` : 'Sua chegada foi registrada.',
      });
      if (navigator.vibrate) navigator.vibrate(120);
    } catch (error) {
      const resposta = error.response?.data || {};
      if (resposta.code === 'NAO_CONFIRMADO') {
        setEstado({ tipo: 'confirmar', titulo: 'Você ainda não confirmou presença', texto: 'Confirme agora e o check-in é feito em seguida.', peladaId: resposta.peladaId });
        return;
      }
      setEstado({ tipo: 'erro', titulo: 'Não foi possível fazer o check-in', texto: resposta.error || error.message || 'Tente de novo.', semRepetir: ['NFC_DESATIVADO', 'NFC_TAG_INVALIDA', 'SEM_PELADA'].includes(resposta.code) });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [codigo]);

  const confirmarEEntrar = async () => {
    setEstado({ tipo: 'processando', titulo: 'Confirmando presença...' });
    try {
      await api.post('v2/confirm-attendance', { userId: user?._id, peladaId: estado.peladaId });
    } catch (error) {
      setEstado({ tipo: 'erro', titulo: 'Não foi possível confirmar a presença', texto: error.response?.data?.error || 'Tente de novo.' });
      return;
    }
    await fazerCheckin();
  };

  useEffect(() => {
    // Uma vez só (o modo estrito do React monta a tela duas vezes em desenvolvimento)
    if (iniciado.current) return;
    iniciado.current = true;
    fazerCheckin();
  }, [fazerCheckin]);

  const estilo = ESTILO[estado.tipo] || ESTILO.aviso;
  const icone = {
    processando: <CircularProgress size={40} sx={{ color: estilo.cor }} />,
    sucesso: <CheckCircleRoundedIcon sx={{ fontSize: 48 }} />,
    erro: <ErrorRoundedIcon sx={{ fontSize: 48 }} />,
    confirmar: <HowToRegRoundedIcon sx={{ fontSize: 48 }} />,
  }[estado.tipo] || <NfcRoundedIcon sx={{ fontSize: 48 }} />;

  return (
    <AppLayout>
      <PageHeader eyebrow="Pelada" title="Check-in por NFC" />
      <SectionCard>
        <Stack alignItems="center" spacing={1.5} sx={{ textAlign: 'center', py: 3 }} aria-live="polite">
          <Box sx={{ width: 88, height: 88, borderRadius: '50%', display: 'grid', placeItems: 'center', bgcolor: estilo.fundo, color: estilo.cor }}>
            {icone}
          </Box>
          <Typography variant="h5">{estado.titulo}</Typography>
          {estado.texto && <Typography variant="body1" color="text.secondary">{estado.texto}</Typography>}

          {estado.tipo !== 'processando' && (
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ pt: 1.5, width: { xs: '100%', sm: 'auto' } }}>
              {estado.tipo === 'confirmar' && (
                <Button variant="contained" size="large" onClick={confirmarEEntrar}>Confirmar e fazer check-in</Button>
              )}
              {estado.tipo === 'erro' && !estado.semRepetir && (
                <Button variant="contained" size="large" onClick={fazerCheckin}>Tentar de novo</Button>
              )}
              <Button variant={estado.tipo === 'sucesso' || estado.tipo === 'aviso' ? 'contained' : 'outlined'} size="large" onClick={() => navigate('/ordem-chegada')}>
                Ver ordem de chegada
              </Button>
            </Stack>
          )}
        </Stack>
      </SectionCard>
    </AppLayout>
  );
};

export default CheckinNfcPage;
